/**
 * Comp `.dashview` — opening a pinned dashboard renders its widgets straight
 * from cache: no agent turn, no waiting. The chip row underneath is the only
 * way to change it, so editing a dashboard is a sentence, not a builder.
 */
import React from 'react';
import { ActionChip } from '../../components/conversations/cards/ActionChip.js';
import { ReadOnlyWidgetGrid } from '../dashboards/ReadOnlyWidgetGrid.js';
import { useDashboard } from '../../hooks/useWorkflowOutputs.js';
import { SimpleComposer } from './SimpleComposer.js';
import { SimpleHelpButton } from './SimpleHelpButton.js';

interface Props {
  dashboardId: string;
  /** Says the nudge/composer text into the conversation surface. */
  onSay: (message: string) => void;
  starting: boolean;
}

const NUDGES: Array<{ label: string; message: string; variant?: 'ghost' }> = [
  { label: 'Add data readiness by asset', message: 'Add data readiness by asset' },
  { label: 'Show strategies as a table', message: 'Show strategies as a table' },
  { label: 'Alert me in Slack when a limit is near', message: 'Alert me in Slack when a limit is near' },
  { label: 'Nothing, looks good', message: 'Nothing, looks good', variant: 'ghost' },
];

export const SimpleDashboardView: React.FC<Props> = ({ dashboardId, onSay, starting }) => {
  const { data, isLoading, isError } = useDashboard(dashboardId);

  const name = data?.dashboard.name ?? '';
  const widgets = data?.dashboard.widgets ?? [];
  const sayAboutDashboard = (message: string) => onSay(`On dashboard “${name}”: ${message}`);

  return (
    <main className="grid min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto]">
      <header className="flex min-w-0 flex-wrap items-center gap-3 border-b px-7 py-3.5">
        <h1 className="m-0 text-[15px] font-semibold text-heading">{name || 'Dashboard'}</h1>
        <span className="flex-1" />
        <SimpleHelpButton />
      </header>

      <section className="overflow-y-auto px-7 pb-3 pt-7">
        <div className="mx-auto flex max-w-[820px] flex-col gap-[22px]">
          <div className="flex flex-col gap-3 border-b border-dashed pb-2.5">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[1.1px] text-faint">
                dashboard · saved view
              </span>
              <h2 className="m-0 mt-0.5 text-[20px] font-semibold tracking-[-0.01em] text-heading">{name}</h2>
            </div>

            {isLoading ? (
              <p className="text-sm text-muted">Loading…</p>
            ) : isError ? (
              <p className="text-sm text-danger">Could not load this dashboard.</p>
            ) : (
              <ReadOnlyWidgetGrid widgets={widgets} />
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {NUDGES.map((n) => (
              <ActionChip
                key={n.message}
                variant={n.variant}
                disabled={starting}
                onClick={() => sayAboutDashboard(n.message)}
              >
                {n.label}
              </ActionChip>
            ))}
          </div>
        </div>
      </section>

      <SimpleComposer
        placeholder="Change this dashboard — “add …”, “show as table”, “alert me in Slack”…"
        onSend={sayAboutDashboard}
        disabled={starting}
      />
    </main>
  );
};
